import { useTicketList } from "@jugl-web/domain-resources/tickets/hooks/useTicketList";
import { TicketInboxFilter } from "@jugl-web/rest-api/tickets/models/common-types/TicketInboxFilter";
import {
  useLocalStorage,
  useSessionStorage,
} from "@jugl-web/utils/hooks/useStorage";
import {
  TICKETS_VIEW_SEARCH_QUERY_KEY,
  TICKETS_VIEW_SORT_KEY,
} from "@jugl-web/utils/storage";
import { useMe } from "@web-src/features/app/hooks/useMe";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { FC, ReactNode, useMemo } from "react";
import {
  TicketSortDirection,
  TicketsViewContext,
  TicketsViewContextValue,
} from "./TicketsViewContext";
import { useSubscribeToTicketsMutations } from "../../hooks/useSubscribeToTicketsMutations";

interface TicketsViewProviderProps {
  children: ReactNode;
  filter: TicketInboxFilter;
}

export const TicketsViewProvider: FC<TicketsViewProviderProps> = ({
  children,
  filter,
}) => {
  const { entityId } = useEntitySelectedProvider();
  const { id: meId = "" } = useMe();

  const [searchQuery, setSearchQuery] = useSessionStorage<string>(
    TICKETS_VIEW_SEARCH_QUERY_KEY,
    ""
  );

  const [sort, setSort] = useLocalStorage<TicketSortDirection>(
    TICKETS_VIEW_SORT_KEY,
    "desc"
  );

  const ticketListQuery = useTicketList({
    entityId,
    filter,
    meId,
    searchQuery,
    sort,
    skip: !meId,
  });

  const { hasMore, setTickets, setTotalCount } = ticketListQuery;

  useSubscribeToTicketsMutations({
    sort,
    hasMore,
    filter,
    meId,
    searchQuery,
    setTickets,
    setTotalCount,
  });

  const contextValue = useMemo<TicketsViewContextValue>(
    () => ({
      ticketListQuery,
      searchQuery,
      sort,
      setSearchQuery,
      setSort,
    }),
    [ticketListQuery, searchQuery, sort, setSearchQuery, setSort]
  );

  return (
    <TicketsViewContext.Provider value={contextValue}>
      {children}
    </TicketsViewContext.Provider>
  );
};
